import "./KDS.scss"

import React from "react"
import request from "request"

import OrderCard from "./OrderCard"

const WINDOW_OPTIONS = [
  { value: 30, label: "Last 30 min" },
  { value: 60, label: "Last 1 hour" },
  { value: 120, label: "Last 2 hours" },
  { value: 180, label: "Last 3 hours" },
  { value: 240, label: "Last 4 hours" },
]

const SEARCH_DEBOUNCE_MS = 300

// Browse and pull back orders that have already been bumped. Mirrors the
// station grid: same card layout, same gestures (tap to recall one unit,
// long-press for the whole item, "Recall all" for the whole order). The
// time selector and search input are server-side; results cap at 100 orders.
class RecallPanel extends React.Component {
  state = { items: [], windowMinutes: 30, search: "", loading: false }

  componentDidMount() {
    this.fetchItems()
  }

  componentWillUnmount() {
    clearTimeout(this.searchDebounce)
  }

  fetchItems = () => {
    const { windowMinutes, search } = this.state
    const params = new URLSearchParams({ window_minutes: String(windowMinutes) })
    if (search.trim()) params.set("q", search.trim())

    this.setState({ loading: true })
    request.get(`/kds/recall?${params.toString()}`).then(({ data }) => {
      this.setState({ items: data.items || [], loading: false })
    }).catch(() => this.setState({ loading: false }))
  }

  handleWindowChange = (e) => {
    this.setState({ windowMinutes: Number(e.target.value) }, this.fetchItems)
  }

  handleSearchChange = (e) => {
    const search = e.target.value
    this.setState({ search })
    clearTimeout(this.searchDebounce)
    this.searchDebounce = setTimeout(this.fetchItems, SEARCH_DEBOUNCE_MS)
  }

  recall = (item) => {
    request.post(`kds/${item.id}/recall`).then(({ data }) => {
      if (data.errors) {
        window.alert(data.errors.join("\n"))
        return
      }
      this.setState((prev) => ({ items: prev.items.filter((i) => i.id !== item.id) }))
    })
  }

  // Recall-all is just per-item recalls fanned out, mirroring how the live
  // grid implements bump-all on an order in OrderCard.
  recallAll = (item) => this.recall(item)

  groupByOrder = () => {
    const groups = new Map()
    this.state.items.forEach((item) => {
      const list = groups.get(item.orderId) || []
      list.push(item)
      groups.set(item.orderId, list)
    })
    // Newest bumps first (matches the server ordering).
    return Array.from(groups.entries()).sort((a, b) => {
      const aT = Math.max(...a[1].map((i) => new Date(i.kdsReadyAt || 0).getTime()))
      const bT = Math.max(...b[1].map((i) => new Date(i.kdsReadyAt || 0).getTime()))
      return bT - aT
    })
  }

  render() {
    const { windowMinutes, search, loading } = this.state
    const groups = this.groupByOrder()

    return (
      <div className="KDSApp">
        <div className="kds-toolbar">
          <select
            className="recall-window-select"
            value={windowMinutes}
            onChange={this.handleWindowChange}
          >
            {WINDOW_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <input
            type="search"
            className="recall-search-input"
            placeholder="Search order # or product"
            value={search}
            onChange={this.handleSearchChange}
          />
          <div className="toolbar-spacer" />
        </div>

        {groups.length === 0 ? (
          <div className="empty-state">
            {loading ? "Loading…" : "Nothing to recall in this window."}
          </div>
        ) : (
          <div className="KDSGrid">
            {groups.map(([orderId, items]) => (
              <OrderCard
                key={orderId}
                items={items}
                onAction={this.recall}
                onActionAll={this.recallAll}
                actionLabel="Recall"
                actionAllLabel="Recall all"
                actionAllColor="yellow"
                actionItemTitle="Tap to recall 1 · Hold to recall all"
                actionItemClassName="is-warning"
                showDismiss={false}
                timerFromField="kdsReadyAt"
                disableAging
              />
            ))}
          </div>
        )}
      </div>
    )
  }
}

export default RecallPanel
